import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { getVolunteers, saveAssignment, getAssignments, getLocations, getShiftTimes, getCongregations } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];

const DAY_TO_WEEKDAY = {
  'Thu-April-9': 'Thursday',
  'Fri-April-10': 'Friday',
  'Sat-April-11': 'Saturday',
  'Sun-April-12': 'Sunday',
};

function ordinal(n) {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

// Format "HH:MM" (24h time input value) → "H:MM AM/PM"
function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Parse shift name like "7-9 AM", "11 AM-1 PM", "1-4 PM" → [startMin, endMin]
function shiftToMinutes(shiftName) {
  const s = shiftName.trim();
  // "H-H AM" or "H-H PM"
  let m = s.match(/^(\d+)-(\d+)\s+(AM|PM)$/i);
  if (m) {
    const base = m[3].toUpperCase() === 'PM' ? 12 * 60 : 0;
    return [base + parseInt(m[1]) * 60, base + parseInt(m[2]) * 60];
  }
  // "H AM-H PM" or "H PM-H PM"
  m = s.match(/^(\d+)\s*(AM|PM)-(\d+)\s*(AM|PM)$/i);
  if (m) {
    const b1 = m[2].toUpperCase() === 'PM' && parseInt(m[1]) < 12 ? 12 * 60 : 0;
    const b2 = m[4].toUpperCase() === 'PM' && parseInt(m[3]) < 12 ? 12 * 60 : 0;
    return [b1 + parseInt(m[1]) * 60, b2 + parseInt(m[3]) * 60];
  }
  return null;
}

// Parse "HH:MM" time input value → minutes since midnight
function timeToMinutes(t) {
  if (!t) return null;
  const [h, min] = t.split(':').map(Number);
  return h * 60 + (min || 0);
}

const DEFAULT_LOCATIONS = [
  'Washington Road',
  'Surrey Center',
  'Downtown Augusta',
  'James Brown Blvd',
  'Riverwalk'
];

function SchedulingGrid({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [extraSlots, setExtraSlots] = useState({}); // { [key]: count beyond 5 }
  const [congregationMap, setCongregationMap] = useState({}); // name → meeting time info
  const [selectedDay, setSelectedDay] = useState(DAYS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedLocations, setExpandedLocations] = useState({});
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [showUnconfirmedOnly, setShowUnconfirmedOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [volData, assignmentData, locationData, shiftTimeData, congData] = await Promise.all([
      getVolunteers(),
      getAssignments(),
      getLocations(),
      getShiftTimes(),
      getCongregations(),
    ]);
    setVolunteers(volData);

    const cmap = {};
    congData.forEach(c => {
      if (c.name) cmap[c.name] = c;
    });
    setCongregationMap(cmap);

    const activeLocations = (locationData.length > 0
      ? locationData.filter(l => l.active).map(l => l.name)
      : DEFAULT_LOCATIONS
    ).sort((a, b) => a.localeCompare(b));
    setLocations(activeLocations);

    const activeTimes = shiftTimeData.filter(s => s.active);
    setShiftTimes(activeTimes);

    const assignmentMap = {};
    const extraMap = {};
    assignmentData.forEach(a => {
      const key = `${a.day}_${a.location}_${a.shiftTime}`;
      assignmentMap[key] = a.volunteers || [];
      const extra = Math.max(0, (a.volunteers || []).length - 5);
      if (extra > 0) extraMap[key] = extra;
    });
    setAssignments(assignmentMap);
    setExtraSlots(extraMap);

    const expanded = {};
    activeLocations.forEach(loc => { expanded[loc] = true; });
    setExpandedLocations(expanded);

    setLoading(false);
  };

  const toggleLocation = (location) => {
    setExpandedLocations(prev => ({ ...prev, [location]: !prev[location] }));
  };

  const getAvailableVolunteers = (day, shiftTime) => {
    const dayKey = day.split('-')[0].toLowerCase() + 'Availability';
    const smpwVolunteers = [];
    const localVolunteers = [];
    volunteers.forEach(v => {
      const avail = v[dayKey];
      let isAvailable = false;
      if (!avail || avail === '' || avail === 'Not Available') isAvailable = false;
      else if (avail === 'Anytime') isAvailable = true;
      else if (Array.isArray(avail)) isAvailable = avail.includes(shiftTime);
      if (isAvailable) {
        if (v.volunteerType === 'smpw') smpwVolunteers.push(v);
        else localVolunteers.push(v);
      }
    });
    const byName = (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');
    return { smpwVolunteers: smpwVolunteers.sort(byName), localVolunteers: localVolunteers.sort(byName) };
  };

  const saveSlots = async (day, location, shiftTime, updated) => {
    setSaving(true);
    const result = await saveAssignment({ day, location, shiftTime, volunteers: updated });
    setSaving(false);
    if (!result.success) {
      alert('Failed to save assignment. Please try again.');
      loadData();
    }
  };

  const handleAssignment = async (day, location, shiftTime, slotIndex, volunteerId) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = assignments[key] || [];
    const updated = [...current];
    // Extend array if needed
    while (updated.length <= slotIndex) updated.push('');
    updated[slotIndex] = volunteerId;
    setAssignments(prev => ({ ...prev, [key]: updated }));
    await saveSlots(day, location, shiftTime, updated);
  };

  const handleAddExtraSlot = (day, location, shiftTime) => {
    const key = `${day}_${location}_${shiftTime}`;
    setExtraSlots(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
  };

  const handleRemoveExtraSlot = async (day, location, shiftTime, slotIndex) => {
    const key = `${day}_${location}_${shiftTime}`;
    const current = [...(assignments[key] || [])];
    current[slotIndex] = '';
    // Trim trailing empty slots, keep minimum 5
    while (current.length > 5 && !current[current.length - 1]) current.pop();
    const newExtra = Math.max(0, current.length - 5);
    setAssignments(prev => ({ ...prev, [key]: current }));
    setExtraSlots(prev => ({ ...prev, [key]: newExtra }));
    await saveSlots(day, location, shiftTime, current);
  };

  const getVolunteer = (id) => volunteers.find(v => v.id === id) || null;

  const checkConflict = (volunteerId, day, location, shiftTime) => {
    for (const loc of locations) {
      if (loc === location) continue;
      const key = `${day}_${loc}_${shiftTime}`;
      if ((assignments[key] || []).includes(volunteerId)) return true;
    }
    return false;
  };

  const getConflictedIds = (day, location, shiftTime) => {
    const conflicted = new Set();
    for (const loc of locations) {
      if (loc === location) continue;
      const key = `${day}_${loc}_${shiftTime}`;
      (assignments[key] || []).filter(Boolean).forEach(id => conflicted.add(id));
    }
    return conflicted;
  };

  // Returns null (no conflict) or { day, time } for the conflicting meeting
  const getMeetingConflict = (volunteer, day, shiftTime) => {
    if (!volunteer?.congregation) return null;
    const cong = congregationMap[volunteer.congregation];
    if (!cong) return null;
    const weekday = DAY_TO_WEEKDAY[day];
    if (!weekday) return null;
    const shiftRange = shiftToMinutes(shiftTime);
    if (!shiftRange) return null;
    const [shiftStart, shiftEnd] = shiftRange;
    const MEETING_DURATION = 120; // assume 2-hour meeting

    if (cong.midweekMeetingDay === weekday && cong.midweekMeetingTime) {
      const ms = timeToMinutes(cong.midweekMeetingTime);
      if (ms !== null && shiftStart < ms + MEETING_DURATION && ms < shiftEnd) {
        return { day: cong.midweekMeetingDay, time: cong.midweekMeetingTime };
      }
    }
    if (cong.weekendMeetingDay === weekday && cong.weekendMeetingTime) {
      const ms = timeToMinutes(cong.weekendMeetingTime);
      if (ms !== null && shiftStart < ms + MEETING_DURATION && ms < shiftEnd) {
        return { day: cong.weekendMeetingDay, time: cong.weekendMeetingTime };
      }
    }
    return null;
  };

  const handleAutoSchedule = async (scopeDay) => {
    setAutoScheduling(true);

    // Running shift count across all assignments (updated as we assign in this pass)
    const shiftCount = {};
    Object.values(assignments).forEach(slotList => {
      slotList.filter(Boolean).forEach(id => {
        shiftCount[id] = (shiftCount[id] || 0) + 1;
      });
    });

    const days = scopeDay ? [scopeDay] : DAYS;
    const updatedAssignments = { ...assignments };
    const toSave = [];

    for (const day of days) {
      const dayShifts = shiftTimes.filter(st => !st.days || st.days.includes(day));
      for (const loc of locations) {
        for (const shiftObj of dayShifts) {
          const shiftTime = shiftObj.name;
          const key = `${day}_${loc}_${shiftTime}`;
          const current = [...(updatedAssignments[key] || [])];
          while (current.length < 5) current.push('');

          // Collect empty LOCAL slot indices (3 and 4 only — leave SMPW slots 0-2 untouched)
          const emptyLocalIndices = [3, 4].filter(i => !current[i]);
          if (emptyLocalIndices.length === 0) continue;

          const { localVolunteers } = getAvailableVolunteers(day, shiftTime);
          const alreadyInKey = new Set(current.filter(Boolean));

          // Build conflicted IDs from updatedAssignments (not original state) so that
          // volunteers assigned earlier in this same pass are correctly excluded
          const conflictedIds = new Set();
          for (const otherLoc of locations) {
            if (otherLoc === loc) continue;
            const otherKey = `${day}_${otherLoc}_${shiftTime}`;
            (updatedAssignments[otherKey] || []).filter(Boolean).forEach(id => conflictedIds.add(id));
          }

          let candidates = localVolunteers.filter(v =>
            !alreadyInKey.has(v.id) &&
            !conflictedIds.has(v.id) &&
            !getMeetingConflict(v, day, shiftTime)
          );

          for (const slotIdx of emptyLocalIndices) {
            if (candidates.length === 0) break;

            const filled = current.filter(Boolean);
            const scored = candidates.map(v => {
              // Tier 1 — Family: same last name + same congregation as anyone already in this slot
              const hasFamily = filled.some(id => {
                const other = volunteers.find(x => x.id === id);
                return other &&
                  v.lastName && other.lastName &&
                  v.lastName.toLowerCase() === other.lastName.toLowerCase() &&
                  v.congregation && other.congregation &&
                  v.congregation === other.congregation;
              });
              // Tier 2 — Congregation: same congregation, different last name (not already counted as family)
              const sameCong = !hasFamily && filled.some(id => {
                const other = volunteers.find(x => x.id === id);
                return other &&
                  v.congregation && other.congregation &&
                  v.congregation === other.congregation;
              });
              return { v, score: shiftCount[v.id] || 0, hasFamily, sameCong };
            });

            // Sort: family first, then same congregation, then fewest total shifts, then alphabetical
            scored.sort((a, b) => {
              if (a.hasFamily !== b.hasFamily) return a.hasFamily ? -1 : 1;
              if (a.sameCong !== b.sameCong) return a.sameCong ? -1 : 1;
              if (a.score !== b.score) return a.score - b.score;
              return (a.v.lastName || '').localeCompare(b.v.lastName || '');
            });

            const picked = scored[0].v;
            current[slotIdx] = picked.id;
            shiftCount[picked.id] = (shiftCount[picked.id] || 0) + 1;
            alreadyInKey.add(picked.id);
            candidates = candidates.filter(c => c.id !== picked.id);
          }

          updatedAssignments[key] = current;
          const original = assignments[key] || [];
          if (current.some((id, i) => id !== (original[i] || ''))) {
            toSave.push({ day, location: loc, shiftTime, volunteers: current });
          }
        }
      }
    }

    setAssignments(updatedAssignments);
    setSaving(true);
    for (const s of toSave) await saveAssignment(s);
    setSaving(false);
    setAutoScheduling(false);
  };

  const DAY_LABEL_SHORT = {
    'Thu-April-9': 'Thu, April 9', 'Fri-April-10': 'Fri, April 10',
    'Sat-April-11': 'Sat, April 11', 'Sun-April-12': 'Sun, April 12',
  };

  const handleExportConfirmed = () => {
    const rows = [['Day', 'Location', 'Shift Time', 'First Name', 'Last Name', 'Congregation', 'Email', 'Phone', 'Confirmed At']];
    DAYS.forEach(day => {
      locations.forEach(loc => {
        shiftTimes.forEach(st => {
          const key = `${day}_${loc}_${st.name}`;
          const shiftId = key.replace(/\s+/g, '_');
          (assignments[key] || []).filter(Boolean).forEach(id => {
            const vol = getVolunteer(id);
            if (!vol) return;
            if (vol[`confirmation_${shiftId}`] === 'confirmed') {
              const confirmedRaw = vol[`confirmationTime_${shiftId}`];
              const confirmedAt = confirmedRaw
                ? new Date(confirmedRaw).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
                : '';
              rows.push([
                DAY_LABEL_SHORT[day] || day, loc, st.name,
                vol.firstName || '', vol.lastName || '', vol.congregation || '',
                vol.email || '', vol.phone || '', confirmedAt,
              ]);
            }
          });
        });
      });
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const now = new Date();
    const datePart = now.toISOString().split('T')[0];
    const timePart = now.toTimeString().slice(0, 8).replace(/:/g, '');
    a.download = `local-confirmed-shifts-${datePart}-${timePart}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Scheduling Grid</h1>
        <p className="page-subtitle">Assign volunteers to cart witnessing locations and time slots for each event day.</p>
      </header>

      <div className="scheduling-content">
        <div className="day-selector">
          {DAYS.map(day => (
            <button
              key={day}
              className={`day-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {day}
            </button>
          ))}
        </div>

        <div className="schedule-controls">
          <button
            onClick={() => {
              const all = {};
              locations.forEach(loc => { all[loc] = true; });
              setExpandedLocations(all);
            }}
            className="btn btn-secondary"
          >
            ⬇️ Expand All
          </button>
          <button
            onClick={() => {
              const all = {};
              locations.forEach(loc => { all[loc] = false; });
              setExpandedLocations(all);
            }}
            className="btn btn-secondary"
          >
            ⬆️ Collapse All
          </button>
          <button
            className="btn btn-primary"
            disabled={autoScheduling}
            onClick={() => handleAutoSchedule(selectedDay)}
            title="Auto-fill empty local slots for the current day only"
          >
            {autoScheduling ? '⏳ Filling…' : '⚡ Auto-Fill Local (This Day)'}
          </button>
          <button
            className="btn btn-secondary"
            disabled={autoScheduling}
            onClick={() => handleAutoSchedule(null)}
            title="Auto-fill empty local slots across all days"
          >
            {autoScheduling ? '⏳ Filling…' : '⚡ Auto-Fill Local (All Days)'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExportConfirmed}
            title="Export confirmed shifts to CSV"
          >
            ⬇️ Export Confirmed
          </button>
          <button
            className={`btn ${showUnconfirmedOnly ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowUnconfirmedOnly(v => !v)}
            title="Show only shifts with at least one unconfirmed volunteer"
          >
            {showUnconfirmedOnly ? '🔴 Unconfirmed Only (ON)' : '🔴 Unconfirmed Only'}
          </button>
        </div>

        {saving && <div className="save-indicator">💾 Saving...</div>}

        <div className="schedule-grid">
          {(() => {
            // Count total event shifts per volunteer across all days/locations
            const volunteerShiftCount = {};
            Object.values(assignments).forEach(slotList => {
              slotList.filter(Boolean).forEach(id => {
                volunteerShiftCount[id] = (volunteerShiftCount[id] || 0) + 1;
              });
            });

            const dayShifts = shiftTimes.filter(st => !st.days || st.days.includes(selectedDay));
            return locations.map(location => {
            const isExpanded = expandedLocations[location] !== false;

            return (
              <div key={location} className="location-section">
                <div
                  className="location-header-clickable"
                  onClick={() => toggleLocation(location)}
                >
                  <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                  <h3 className="location-header-title">📍 {location}</h3>
                  <span className="location-shift-count">
                    {dayShifts.length} shifts
                  </span>
                </div>

                {isExpanded && (
                  <div className="shifts-container">
                    {dayShifts.map(shiftObj => {
                      const shiftTime = shiftObj.name;
                      const key = `${selectedDay}_${location}_${shiftTime}`;
                      const shiftId = key.replace(/\s+/g, '_');
                      const currentAssignments = assignments[key] || [];

                      // Unconfirmed filter: skip shifts where every assigned volunteer is confirmed
                      if (showUnconfirmedOnly) {
                        const assigned = currentAssignments.filter(Boolean);
                        if (assigned.length === 0) return null;
                        const allConfirmed = assigned.every(id => {
                          const vol = getVolunteer(id);
                          return vol && vol[`confirmation_${shiftId}`] === 'confirmed';
                        });
                        if (allConfirmed) return null;
                      }
                      const extra = extraSlots[key] || 0;
                      const totalSlots = 5 + extra;
                      const filledCount = currentAssignments.filter(Boolean).length;
                      const conflictedIds = getConflictedIds(selectedDay, location, shiftTime);
                      const { smpwVolunteers, localVolunteers } = getAvailableVolunteers(selectedDay, shiftTime);
                      const allAvailable = [...smpwVolunteers, ...localVolunteers];

                      return (
                        <div key={shiftTime} className="shift-row">
                          <div className="shift-header">
                            <div className="shift-time">⏰ {shiftTime}</div>
                            <div className={`shift-capacity ${filledCount >= totalSlots ? 'full' : ''}`}>
                              {filledCount}/{totalSlots} filled
                            </div>
                          </div>

                          <div className="volunteer-slots">
                            {Array.from({ length: totalSlots }, (_, slotIndex) => {
                              const assignedId = currentAssignments[slotIndex];
                              const assignedVol = assignedId ? getVolunteer(assignedId) : null;
                              const hasConflict = assignedId && checkConflict(assignedId, selectedDay, location, shiftTime);
                              const confStatus = assignedVol ? (assignedVol[`confirmation_${shiftId}`] || 'pending') : null;
                              const isExtraSlot = slotIndex >= 5;

                              // Base slot labels
                              let label = slotIndex < 3
                                ? `🔵 SMPW ${slotIndex + 1}${slotIndex === 0 ? ' (Keyman)' : ''}`
                                : slotIndex < 5
                                  ? `🟢 Local ${slotIndex - 2}`
                                  : `➕ Extra ${slotIndex - 4}`;

                              // Volunteers already assigned to other slots in this shift
                              const alreadyUsedInShift = new Set(
                                currentAssignments.filter((id, i) => i !== slotIndex && id)
                              );

                              // Available volunteers per slot type, excluding those already in another slot
                              let available;
                              if (isExtraSlot) {
                                available = allAvailable;
                              } else if (slotIndex < 3) {
                                available = slotIndex === 0 ? smpwVolunteers.filter(v => v.isKeyman) : smpwVolunteers;
                              } else {
                                available = localVolunteers;
                              }
                              available = available.filter(v => !alreadyUsedInShift.has(v.id));
                              // Always include the currently-assigned volunteer even if they're the wrong type
                              // for this slot — this ensures the dropdown can be cleared or corrected
                              if (assignedVol && !available.find(v => v.id === assignedId)) {
                                available = [{ ...assignedVol, _slotMismatch: true }, ...available];
                              }

                              return (
                                <div key={slotIndex} className="volunteer-slot">
                                  <label>
                                    {label}:
                                    {isExtraSlot && (
                                      <button
                                        className="slot-remove-btn"
                                        onClick={() => handleRemoveExtraSlot(selectedDay, location, shiftTime, slotIndex)}
                                        title="Remove this slot"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </label>
                                  <select
                                    value={assignedId || ''}
                                    onChange={e => handleAssignment(selectedDay, location, shiftTime, slotIndex, e.target.value)}
                                    className={hasConflict ? 'conflict' : ''}
                                  >
                                    <option value="">-- Select Volunteer --</option>
                                    {available.length > 0 && available.map(v => {
                                      const sc = volunteerShiftCount[v.id] || 0;
                                      return (
                                        <option key={v.id} value={v.id}>
                                          {v._slotMismatch ? '⚠ Wrong slot type — ' : ''}
                                          {conflictedIds.has(v.id) ? '⚠ CONFLICT — ' : ''}
                                          {v.lastName}, {v.firstName} ({v.congregation || 'N/A'}){v.isKeyman ? ' 🔑' : ''}{v.isDeafHH ? ' 🦻' : ''}{sc > 0 ? ` — ${sc} shift${sc !== 1 ? 's' : ''}` : ''}
                                        </option>
                                      );
                                    })}
                                  </select>

                                  {hasConflict && (
                                    <span className="conflict-warning">⚠️ Also assigned elsewhere at this time!</span>
                                  )}

                                  {assignedVol && (() => {
                                    const meetingConflict = getMeetingConflict(assignedVol, selectedDay, shiftTime);
                                    return (
                                      <div className="assigned-volunteer-info">
                                        <div className="assigned-name">
                                          {assignedVol.lastName}, {assignedVol.firstName}
                                          {assignedVol.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing — email/text only" style={{ marginLeft: 5, fontSize: '11px' }}>🦻 D/HH</span>}
                                        </div>
                                        {assignedVol.phone && !assignedVol.isDeafHH && <div className="assigned-phone">📞 {assignedVol.phone}</div>}
                                        {assignedVol.phone && assignedVol.isDeafHH && <div className="assigned-phone" style={{ color: '#7c3aed' }}>✉ text only: {assignedVol.phone}</div>}
                                        {assignedVol.congregation && <div className="assigned-congregation">{assignedVol.congregation}</div>}
                                        {assignedVol.spouseName && <div className="assigned-spouse">👥 {assignedVol.spouseName}</div>}
                                        {(() => {
                                          const sc = volunteerShiftCount[assignedVol.id] || 0;
                                          if (!sc) return null;
                                          const cls = sc === 1 ? 'shift-count-badge one' : sc === 2 ? 'shift-count-badge two' : 'shift-count-badge many';
                                          return <div className={cls}>{ordinal(sc)} shift of event</div>;
                                        })()}
                                        {meetingConflict && (
                                          <span
                                            className="meeting-conflict-warning"
                                            title={`${assignedVol.congregation} meets on ${meetingConflict.day} at ${formatTime12(meetingConflict.time)}`}
                                          >
                                            Meeting conflict — {meetingConflict.day} {formatTime12(meetingConflict.time)}
                                          </span>
                                        )}
                                        {confStatus && confStatus !== 'pending' && (
                                          <span className={`conf-badge conf-badge-${confStatus}`}>
                                            {confStatus === 'confirmed' && '✅ Confirmed'}
                                            {confStatus === 'cannot-make' && '❌ Cannot Make'}
                                            {confStatus === 'left-vm' && '📞 Left VM'}
                                            {confStatus === 'no-response' && '🔇 No Response'}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              );
                            })}
                          </div>

                          <button
                            className="slot-add-btn"
                            onClick={() => handleAddExtraSlot(selectedDay, location, shiftTime)}
                          >
                            + Add Slot
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
          })()}
        </div>
      </div>
    </div>
  );
}

export default SchedulingGrid;
